import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { NetworkCanvas } from "@/components/NetworkCanvas";
import { CheckCircle, XCircle, Spinner, ArrowRight } from "@phosphor-icons/react";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 12;

function Shell({ children }) {
  return (
    <div className="relative min-h-screen grid place-items-center bg-[#030305] text-white overflow-hidden px-6">
      <NetworkCanvas className="absolute inset-0 h-full w-full opacity-60" density={70} />
      <div className="absolute inset-0 qs-grid opacity-[0.1]" />
      <div className="relative w-full max-w-md rounded-2xl qs-glass border border-white/10 p-8 text-center" data-testid="payment-result">
        {children}
      </div>
    </div>
  );
}

export function PaymentSuccess() {
  const [state, setState] = useState("checking"); // checking | success | error | timeout
  const navigate = useNavigate();
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      if (attempts.current >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState("success");
          return;
        }
        if (data.status === "failed" || data.status === "expired") {
          setState("error");
          return;
        }
      } catch (e) {
        /* keep polling */
      }
      timer = setTimeout(poll, POLL_INTERVAL);
    };
    poll();
    return () => clearTimeout(timer);
  }, []);

  if (state === "checking") {
    return (
      <Shell>
        <Spinner size={44} className="text-[#2DD4BF] mx-auto animate-spin" />
        <h1 className="text-2xl font-black mt-6">Confirming payment…</h1>
        <p className="text-[#94A3B8] mt-2 text-sm">Verifying your license activation. This only takes a moment.</p>
      </Shell>
    );
  }
  if (state === "success") {
    return (
      <Shell>
        <CheckCircle size={52} weight="fill" className="text-emerald-400 mx-auto" />
        <h1 className="text-2xl font-black mt-6">License activated</h1>
        <p className="text-[#94A3B8] mt-2 text-sm">Your Q-Core license is now active. Welcome aboard.</p>
        <button
          onClick={() => navigate("/dashboard")}
          data-testid="payment-goto-dashboard"
          className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2DD4BF] text-[#030305] font-semibold hover:bg-white transition-colors"
        >
          Go to Dashboard <ArrowRight size={16} weight="bold" />
        </button>
        <p className="mt-5 font-mono text-[10px] text-amber-300/70">Trading involves risk. License provides software access, not guaranteed returns.</p>
      </Shell>
    );
  }
  if (state === "timeout") {
    return (
      <Shell>
        <Spinner size={44} className="text-amber-400 mx-auto" />
        <h1 className="text-2xl font-black mt-6">Still processing</h1>
        <p className="text-[#94A3B8] mt-2 text-sm">Your payment is taking a little longer than usual. Check your dashboard shortly.</p>
        <button onClick={() => navigate("/dashboard")} className="mt-7 px-6 py-3 rounded-full border border-white/15 text-white hover:bg-white/5 transition-colors">
          Go to Dashboard
        </button>
      </Shell>
    );
  }
  return (
    <Shell>
      <XCircle size={52} weight="fill" className="text-red-400 mx-auto" />
      <h1 className="text-2xl font-black mt-6">Something went wrong</h1>
      <p className="text-[#94A3B8] mt-2 text-sm">We couldn't confirm your payment. No license was activated.</p>
      <button onClick={() => navigate("/pricing")} className="mt-7 px-6 py-3 rounded-full border border-white/15 text-white hover:bg-white/5 transition-colors">
        Back to License Options
      </button>
    </Shell>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <Shell>
      <XCircle size={52} weight="fill" className="text-[#94A3B8] mx-auto" />
      <h1 className="text-2xl font-black mt-6">Checkout cancelled</h1>
      <p className="text-[#94A3B8] mt-2 text-sm">No charge was made. You can pick a license whenever you're ready.</p>
      <button
        onClick={() => navigate("/pricing")}
        data-testid="payment-back-pricing"
        className="mt-7 px-6 py-3 rounded-full bg-[#2DD4BF] text-[#030305] font-semibold hover:bg-white transition-colors"
      >
        Back to License Options
      </button>
    </Shell>
  );
}
